// Prevent duplicate execution if script is loaded multiple times
if (window.__buildOrderInitialized) {
  // Script already loaded, exit early
} else {
  window.__buildOrderInitialized = true;

  const CART_UPDATE_EVENT = 'cart:update';
  const CART_ERROR_EVENT = 'cart:error';

  /** Line item property on build-order main products when a free gift is included; must match component-cart-items.js */
  const BUILD_ORDER_PARENT_LINE_PROPERTY_KEY = '_build_order_parent';

/**
 * Convert display value to cart quantity using step (e.g. display 10, step 10 → 1).
 * @param {number} displayQuantity
 * @param {number} step
 */
const displayToCartQty = (displayQuantity, step) => {
  const s = Math.max(1, Number(step) || 1);
  const normalized = Number.isFinite(displayQuantity) ? displayQuantity : s;
  return Math.max(1, Math.round(normalized / s));
};

/**
 * Normalize display quantity to the nearest multiple of step, minimum step.
 * @param {number} displayQuantity
 * @param {number} step
 */
const normalizeDisplayQty = (displayQuantity, step) => {
  const s = Math.max(1, Number(step) || 1);
  const normalized = Number.isFinite(displayQuantity) ? displayQuantity : s;
  return Math.max(s, Math.round(normalized / s) * s);
};

/**
 * Build Order Component
 * Manages quantity tracking, cup calculations, and multi-product cart add with Firmhouse selling plans
 * @param {HTMLElement} container
 */
class BuildOrderComponent {
  /**
   * @param {Element} container
   */
  constructor(container) {
    this.container = container;
    /** @type {HTMLElement | null} */
    const inner = /** @type {HTMLElement | null} */ (this.container.querySelector('.build-order-container'));
    this.minimumCups = inner ? parseInt(inner.dataset.minimumCups, 10) || 60 : 60;
    this.maximumCups = this.minimumCups;
    this.displayStep = Math.max(1, parseInt(inner?.dataset.displayStep, 10) || 1);
    this.orderButtonLabel = inner?.dataset.orderButtonLabel ?? 'Bestel';
    this.addMoreItemsLabelTemplate = inner?.dataset.addMoreItemsLabel ?? 'Voeg nog {{ count }} items toe aan je box';
    this.loadingLabel = inner?.dataset.loadingLabel ?? 'Bezig...';
    this.errorGeneric = inner?.dataset.errorGeneric ?? 'Er is een fout opgetreden bij het plaatsen van je bestelling.';
    this.errorAddMoreCups = inner?.dataset.errorAddMoreCups ?? 'Voeg nog {{ count }} cups toe om door te gaan.';
    this.errorAddMoreBeans = inner?.dataset.errorAddMoreBeans ?? 'Voeg nog {{ count }} zakken toe om door te gaan.';
    this.errorTooManyCups = inner?.dataset.errorTooManyCups ?? 'Verwijder {{ count }} cups om door te gaan.';
    this.errorTooManyBeans = inner?.dataset.errorTooManyBeans ?? 'Verwijder {{ count }} zakken om door te gaan.';
    this.errorTooManyItems = inner?.dataset.errorTooManyItems ?? 'Verwijder {{ count }} producten om door te gaan.';
    this.errorNoProducts = inner?.dataset.errorNoProducts ?? 'Geen producten geselecteerd.';
    this.addMoreCupsLabelTemplate = inner?.dataset.addMoreCupsLabel ?? 'Voeg nog {{ count }} cups toe';
    this.cupsSelectedLabelTemplate = inner?.dataset.cupsSelectedLabel ?? 'Je hebt {{ count }} cups geselecteerd';
    this.addMoreBeansLabelTemplate = inner?.dataset.addMoreBeansLabel ?? 'Voeg nog {{ count }} zakken toe';
    this.beansSelectedLabelTemplate = inner?.dataset.beansSelectedLabel ?? 'Je hebt {{ count }} zakken geselecteerd';
    this.isCups = inner?.dataset.isCups === 'true';
    this.addToCartBehavior = inner?.dataset.addToCartBehavior ?? 'cart_page';
    this.addToCartLabel = inner?.dataset.addToCartLabel ?? 'Add to cart';
    this.oneTimePriceSuffix = inner?.dataset.oneTimePriceSuffix ?? '';
    this.subscriptionDiscountCode = inner?.dataset.subscriptionDiscountCode?.trim() ?? '';
    this.extraProductVariantId = inner?.dataset.extraProductVariantId?.trim() ?? '';
    this.giftLineItemPropertyKey = inner?.dataset.giftLineItemPropertyKey?.trim() || '_gift_discount_code';
    this.brievenbusMax = Math.max(0, parseInt(inner?.dataset.brievenbusMax, 10) || 6);
    this.shippingBrievenbusLabel = inner?.dataset.shippingBrievenbusLabel ?? 'Het wordt verzonden als brievenbus pakket.';
    this.shippingPakketLabelTemplate = inner?.dataset.shippingPakketLabel ?? 'Let op: wordt verzonden als pakket. Max {{ count }} tot brievenbus pakket.';
    this.products = new Map();
    this.frequency = '2';
    this.orderType = (inner?.dataset.orderTypeActive === 'onetime') ? 'onetime' : 'recurring';

    this.init();
  }

  init() {
    this.setupQuantitySelectors();
    this.setupFrequencySelector();
    this.setupOrderTypeToggle();
    this.setupOrderButton();
    this.updateTotals();
    requestAnimationFrame(() => this.#syncProductForms());
  }

  /**
   * Setup toggle between recurring and one-time order type; expand/collapse content panels and update circles.
   */
  setupOrderTypeToggle() {
    const inner = this.container.querySelector('.build-order-container');
    const recurringContent = this.container.querySelector('.build-order-recurring-content');
    const onetimeContent = this.container.querySelector('.build-order-onetime-content');
    const toggleRows = this.container.querySelectorAll('.build-order-toggle-row');
    if (toggleRows.length === 0) return;

    const setOrderType = (type) => {
      this.orderType = type;
      if (inner) inner.dataset.orderTypeActive = type;

      const isRecurring = type === 'recurring';
      if (recurringContent) {
        recurringContent.classList.toggle('is-collapsed', !isRecurring);
        recurringContent.setAttribute('aria-hidden', isRecurring ? 'true' : 'false');
      }
      if (onetimeContent) {
        onetimeContent.classList.toggle('is-collapsed', isRecurring);
        onetimeContent.setAttribute('aria-hidden', isRecurring ? 'true' : 'false');
      }

      const subscriptionPanel = this.container.querySelector('.build-order-subscription');
      const onetimePanel = this.container.querySelector('.build-order-onetime-outer');
      if (subscriptionPanel) subscriptionPanel.classList.toggle('is-active', isRecurring);
      if (onetimePanel) onetimePanel.classList.toggle('is-active', !isRecurring);

      toggleRows.forEach((row) => {
        const circle = row.querySelector('.build-order-toggle-circle');
        const rowType = row.dataset.orderType;
        const active = rowType === type;
        if (circle) circle.classList.toggle('is-active', active);
        row.setAttribute('aria-pressed', active ? 'true' : 'false');
        row.setAttribute('aria-expanded', active ? 'true' : 'false');
      });

      this.updateTotals();
    };

    toggleRows.forEach((row) => {
      row.addEventListener('click', () => {
        const type = row.dataset.orderType;
        if (type && type !== this.orderType) setOrderType(type);
      });
    });
  }

  /**
   * Setup quantity selector event listeners
   */
  setupQuantitySelectors() {
    /** @type {NodeListOf<HTMLInputElement>} */
    const quantityInputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.container.querySelectorAll('.nf-quantity-input')
    );
    /** @type {NodeListOf<HTMLButtonElement>} */
    const minusButtons = /** @type {NodeListOf<HTMLButtonElement>} */ (
      this.container.querySelectorAll('.nf-quantity-btn-minus')
    );
    /** @type {NodeListOf<HTMLButtonElement>} */
    const plusButtons = /** @type {NodeListOf<HTMLButtonElement>} */ (
      this.container.querySelectorAll('.nf-quantity-btn-plus')
    );

    const step = this.displayStep;
    const inputCount = quantityInputs.length;

    /** @type {number[]} */
    const distributedInitialValues = [];

    if (inputCount > 0) {
      if (!this.isCups) {
        // Beans/tea: fill products one-by-one with `step` until the minimum is reached; the rest start at 0.
        // If there are fewer products than needed, the first product absorbs any remaining shortage.
        let remaining = this.minimumCups;

        for (let i = 0; i < inputCount; i++) {
          if (remaining >= step) {
            distributedInitialValues[i] = step;
            remaining -= step;
          } else {
            distributedInitialValues[i] = 0;
          }
        }

        if (remaining > 0) {
          distributedInitialValues[0] += remaining;
        }
      } else {
        const minimumCups = this.minimumCups;
        const totalSteps = Math.max(1, Math.floor(minimumCups / step));
        const baseStepsPerItem = Math.floor(totalSteps / inputCount);

        for (let i = 0; i < inputCount; i++) {
          distributedInitialValues[i] = baseStepsPerItem * step;
        }

        let remainderSteps = totalSteps - baseStepsPerItem * inputCount;

        if (inputCount === 1) {
          distributedInitialValues[0] = totalSteps * step;
        } else if (remainderSteps > 0) {
          const extraFirstSteps = Math.ceil(remainderSteps / 2);
          const extraSecondSteps = remainderSteps - extraFirstSteps;

          distributedInitialValues[0] += extraFirstSteps * step;

          if (inputCount > 1 && extraSecondSteps > 0) {
            distributedInitialValues[1] += extraSecondSteps * step;
          }
        }

        const totalAllocated = distributedInitialValues.reduce((sum, value) => sum + value, 0);
        if (totalAllocated < minimumCups) {
          const shortageSteps = Math.ceil((minimumCups - totalAllocated) / step);
          distributedInitialValues[0] += shortageSteps * step;
        }
      }
    }

    quantityInputs.forEach((input, index) => {
      const productId = input.dataset.productId;
      if (!productId) return;

      const fallbackValue = this.#normalizeQty(parseInt(input.value, 10));
      const initialValue =
        distributedInitialValues.length === inputCount
          ? distributedInitialValues[index]
          : fallbackValue;

      input.value = String(initialValue);
      this.products.set(productId, { quantity: initialValue });

      input.addEventListener('input', (e) => {
        if (!(e.target instanceof HTMLInputElement)) return;
        const value = this.#normalizeQty(parseInt(e.target.value, 10));
        this.handleQuantityChange(productId, value);
      });

      input.addEventListener('blur', (e) => {
        if (!(e.target instanceof HTMLInputElement)) return;
        const value = this.#normalizeQty(parseInt(e.target.value, 10));
        e.target.value = String(value);
        this.handleQuantityChange(productId, value);
      });
    });

    minusButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const productId = button.dataset.productId;
        if (!productId) return;
        const input = this.container.querySelector(
          `.nf-quantity-input[data-product-id="${productId}"]`
        );
        /** @type {HTMLInputElement | null} */
        const inputEl = /** @type {HTMLInputElement | null} */ (input);
        if (inputEl) {
          const currentValue = this.#normalizeQty(parseInt(inputEl.value, 10));
          const newValue = this.#normalizeQty(currentValue - this.displayStep);
          inputEl.value = String(newValue);
          this.handleQuantityChange(productId, newValue);
        }
      });
    });

    plusButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const productId = button.dataset.productId;
        if (!productId) return;
        const input = this.container.querySelector(
          `.nf-quantity-input[data-product-id="${productId}"]`
        );
        /** @type {HTMLInputElement | null} */
        const inputEl = /** @type {HTMLInputElement | null} */ (input);
        if (inputEl) {
          const currentValue = this.#normalizeQty(parseInt(inputEl.value, 10));
          const newValue = currentValue + this.displayStep;
          inputEl.value = String(newValue);
          this.handleQuantityChange(productId, newValue);
        }
      });
    });
  }

  /**
   * Setup frequency selector
   */
  setupFrequencySelector() {
    /** @type {HTMLSelectElement | null} */
    const frequencySelect = /** @type {HTMLSelectElement | null} */ (
      this.container.querySelector('.build-order-frequency-select')
    );
    if (frequencySelect) {
      this.frequency = frequencySelect.value;
      this.#updateSavePercentage();
      frequencySelect.addEventListener('change', (e) => {
        if (!(e.target instanceof HTMLSelectElement)) return;
        this.frequency = e.target.value;
        this.#syncProductForms();
        this.#updateSavePercentage();
        this.updateTotals();
      });
    }
  }

  /**
   * Update save-percentage value spans and per-period label from the selected frequency option
   */
  #updateSavePercentage() {
    const frequencySelect = this.container.querySelector('.build-order-frequency-select');
    if (!frequencySelect) return;
    const selectedOption = frequencySelect.options[frequencySelect.selectedIndex];
    const saveValue = selectedOption?.getAttribute('data-save-percentage');
    if (saveValue !== null && saveValue !== undefined) {
      const valueSpans = this.container.querySelectorAll('.save-percentage-value');
      for (const span of valueSpans) {
        span.textContent = saveValue;
      }
    }
    const perPeriod = selectedOption?.getAttribute('data-per-period');
    if (perPeriod !== null && perPeriod !== undefined) {
      const perPeriodEl = this.container.querySelector('.build-order-per-period');
      if (perPeriodEl) perPeriodEl.textContent = perPeriod;
    }
  }

  /**
   * Normalize a display quantity to the nearest step multiple, floored at 0 so items can be opted out.
   * @param {number} value
   * @returns {number}
   */
  #normalizeQty(value) {
    const s = this.displayStep;
    const n = Number.isFinite(value) ? value : 0;
    return Math.max(0, Math.round(n / s) * s);
  }

  /**
   * Convert a display quantity to cart quantity, returning 0 when the display quantity is 0.
   * @param {number} displayQty
   * @returns {number}
   */
  #toCartQty(displayQty) {
    if (displayQty <= 0) return 0;
    return displayToCartQty(displayQty, this.displayStep);
  }

  /**
   * Get selling plan ID from the selected frequency option (used for name="selling_plan" on load and on change)
   */
  #getSelectedSellingPlanId() {
    if (this.orderType === 'onetime') return '';
    /** @type {HTMLSelectElement | null} */
    const frequencySelect = /** @type {HTMLSelectElement | null} */ (
      this.container.querySelector('.build-order-frequency-select')
    );
    if (!frequencySelect || frequencySelect.options.length === 0) return '';
    const selectedOption = frequencySelect.options[frequencySelect.selectedIndex];
    const id = selectedOption?.getAttribute('data-selling-plan-id');
    return (id && String(id).trim()) || '';
  }

  /**
   * Setup order button
   */
  setupOrderButton() {
    /** @type {HTMLButtonElement | null} */
    const orderButton = /** @type {HTMLButtonElement | null} */ (
      this.container.querySelector('.build-order-submit-btn[data-order-type="recurring"]')
    );
    if (orderButton) {
      orderButton.addEventListener('click', (e) => {
        e.preventDefault();
        if (orderButton.disabled) return;
        this.handleOrderSubmit();
      });
    }
    const onetimeButton = this.container.querySelector('.build-order-onetime-submit-btn');
    if (onetimeButton) {
      onetimeButton.addEventListener('click', (e) => {
        e.preventDefault();
        if (onetimeButton.disabled) return;
        this.handleOrderSubmit();
      });
    }
  }

  /**
   * Handle quantity change for a product
   */
  /**
   * @param {string} productId
   * @param {number} newQuantity
   */
  handleQuantityChange(productId, newQuantity) {
    const product = this.products.get(productId);
    if (product) {
      product.quantity = newQuantity;
      this.updateTotals();
    }
  }

  /**
   * Calculate total cups/beans - read directly from DOM inputs to ensure accuracy
   */
  calculateTotalCups() {
    let total = 0;
    const quantityInputs = this.container.querySelectorAll('.nf-quantity-input');
    quantityInputs.forEach((input) => {
      const displayQty = this.#normalizeQty(parseInt(input.value, 10));
      total += displayQty;
    });
    return total;
  }

  /**
   * Update info bar message
   */
  updateInfoBar() {
    const totalCups = this.calculateTotalCups();
    const infoBar = this.container.querySelector('.build-order-info-text');
    const infoBarWrapper = this.container.querySelector('.build-order-info-bar');

    if (!infoBar) return;

    const underMin = totalCups < this.minimumCups;

    if (underMin) {
      const template = this.isCups ? this.addMoreCupsLabelTemplate : this.addMoreBeansLabelTemplate;
      infoBar.textContent = template.replace(/\{\{\s*count\s*\}\}/, String(this.minimumCups - totalCups));
      infoBarWrapper?.classList.remove('is-over-max');
    } else {
      const template = this.isCups ? this.cupsSelectedLabelTemplate : this.beansSelectedLabelTemplate;
      infoBar.textContent = template.replace(/\{\{\s*count\s*\}\}/, String(totalCups));
      infoBarWrapper?.classList.remove('is-over-max');
    }
  }

  /**
   * Update submit button state and label based on minimum cups (recurring or one-time depending on orderType).
   */
  updateSubmitButton() {
    const totalCups = this.calculateTotalCups();
    const meetsMinimum = totalCups >= this.minimumCups;
    const canOrder = meetsMinimum;

    if (this.orderType === 'recurring') {
      const orderButton = /** @type {HTMLButtonElement | null} */ (
        this.container.querySelector('.build-order-submit-btn[data-order-type="recurring"]')
      );
      const labelEl = this.container.querySelector('.build-order-submit-btn-label');
      if (!orderButton || !labelEl) return;
      orderButton.disabled = !canOrder;
      labelEl.textContent = meetsMinimum
        ? this.orderButtonLabel
        : this.addMoreItemsLabelTemplate.replace(/\{\{\s*count\s*\}\}/, String(this.minimumCups - totalCups));
      return;
    }

    const onetimeButton = this.container.querySelector('.build-order-onetime-submit-btn');
    const onetimeLabelEl = this.container.querySelector('.build-order-onetime-submit-btn-label');
    if (!onetimeButton || !onetimeLabelEl) return;
    onetimeButton.disabled = !canOrder;
    onetimeLabelEl.textContent = meetsMinimum
      ? this.addToCartLabel
      : this.addMoreItemsLabelTemplate.replace(/\{\{\s*count\s*\}\}/, String(this.minimumCups - totalCups));
  }

  /**
   * Calculate total price and update the price display. One-time: sum of variant price × quantity; recurring: selling-plan price × quantity.
   * One-time total (build-order-onetime-total-value) is always computed from quantity, like build-order-total-price-original.
   */
  updateTotalPrice() {
    const inner = this.container.querySelector('.build-order-container');
    if (!inner?.dataset.moneyFormat) return;

    const items = this.container.querySelectorAll('.build-order-product-item');
    const format = inner.dataset.moneyFormat;

    let onetimeTotalCents = 0;
    for (const item of items) {
      const onetimePrice = parseInt(item.dataset.variantPriceOnetime, 10) || 0;
      const qtyInput = item.querySelector('.nf-quantity-input');
      const displayQty = this.#normalizeQty(parseInt(qtyInput?.value, 10));
      const cartQty = this.#toCartQty(displayQty);
      onetimeTotalCents += onetimePrice * cartQty;
    }
    const onetimeFormatted = this.#formatMoney(onetimeTotalCents, format);
    const allOnetimeTotals = this.container.querySelectorAll('.build-order-onetime-total-value');
    for (const el of allOnetimeTotals) {
      el.textContent = onetimeFormatted;
    }
    const previewEl = this.container.querySelector('.build-order-onetime-price-preview');
    if (previewEl) {
      const previewSuffix = previewEl.querySelector('.build-order-per-period');
      if (previewSuffix && this.oneTimePriceSuffix) previewSuffix.textContent = this.oneTimePriceSuffix;
    }

    if (this.orderType === 'onetime') return;

    const planKey = `variantPricePlan${this.frequency}`;
    let totalCents = 0;
    for (const item of items) {
      const planPrice =
        parseInt(item.dataset[planKey], 10) || parseInt(item.dataset.variantPrice, 10) || 0;
      const qtyInput = item.querySelector('.nf-quantity-input');
      const displayQty = this.#normalizeQty(parseInt(qtyInput?.value, 10));
      const cartQty = this.#toCartQty(displayQty);
      totalCents += planPrice * cartQty;
    }

    const originalFormatted = onetimeFormatted;
    const discountedFormatted = this.#formatMoney(totalCents, format);

    const originalEl = this.container.querySelector('.build-order-total-price-original');
    const priceEl = this.container.querySelector('.build-order-total-price-value');
    if (originalEl) originalEl.textContent = originalFormatted;
    if (priceEl) priceEl.textContent = discountedFormatted;
  }

  /**
   * Format cents as money string using shop format
   * @param {number} totalCents
   * @param {string} format
   * @returns {string}
   */
  #formatMoney(totalCents, format) {
    if (!format) return '';
    const major = totalCents / 100;
    const withComma = major.toFixed(2).replace('.', ',');
    return format
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, String(Math.round(major)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withComma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(major)))
      .replace(/\{\{\s*amount\s*\}\}/, withComma);
  }

  /**
   * Update all totals and UI elements
   */
  updateTotals() {
    this.updateInfoBar();
    this.updateSubmitButton();
    this.updateShippingInfo();
    this.updateReceiveCount();
    this.updateTotalPrice();
  }

  /**
   * Update "Ontvang X doosjes/zakken" with current total quantity.
   */
  updateReceiveCount() {
    const total = this.calculateTotalCups();
    const els = this.container.querySelectorAll('.build-order-receive-count');
    for (const el of els) {
      el.textContent = String(total);
    }
  }

  /**
   * Update shipping info message: brievenbus pakket below max, pakket warning above.
   */
  updateShippingInfo() {
    const totalCups = this.calculateTotalCups();
    const isBrievenbus = this.brievenbusMax > 0 && totalCups <= this.brievenbusMax;
    const text = isBrievenbus
      ? this.shippingBrievenbusLabel
      : this.shippingPakketLabelTemplate.replace(/\{\{\s*count\s*\}\}/, String(this.brievenbusMax));

    const boxes = this.container.querySelectorAll('.build-order-shipping-info');
    for (const box of boxes) {
      const textEl = box.querySelector('.build-order-shipping-info-text');
      if (textEl) {
        textEl.textContent = text;
      }
      box.classList.toggle('build-order-shipping-info--pakket', !isBrievenbus);
    }
  }

  /**
   * Sync hidden form inputs (quantity, selling_plan) from UI. selling_plan = selected option from build-order-frequency-select.
   */
  #syncProductForms() {
    const sellingPlanId = this.#getSelectedSellingPlanId();
    const productItems = this.container.querySelectorAll('.build-order-product-item');

    for (const item of productItems) {
      const qtyInput = item.querySelector('.nf-quantity-input');
      const displayQuantity = this.#normalizeQty(parseInt(qtyInput?.value, 10));
      const cartQuantity = this.#toCartQty(displayQuantity);

      const formComponent = item.querySelector('product-form-component');
      const root = formComponent?.shadowRoot ?? formComponent;
      const form = root?.querySelector?.('.build-order-product-form') ?? item.querySelector('.build-order-product-form');

      if (!form) continue;

      const qtyFormInput = form.querySelector('.build-order-form-quantity');
      const sellingPlanInput = form.querySelector('input[name="selling_plan"], .build-order-form-selling-plan, .selected-selling-plan-id');

      if (qtyFormInput) qtyFormInput.value = String(cartQuantity);
      if (sellingPlanInput) sellingPlanInput.value = sellingPlanId;
    }
  }

  /**
   * Show inline error message in the active panel (recurring or one-time).
   * @param {string} message
   * @param {boolean} [useOnetimePanel]
   */
  #showError(message, useOnetimePanel = false) {
    const recurringError = this.container.querySelector('.build-order-error');
    const onetimeError = this.container.querySelector('.build-order-error-onetime');
    const target = useOnetimePanel ? onetimeError : recurringError;
    const other = useOnetimePanel ? recurringError : onetimeError;
    if (target) {
      target.textContent = message;
      target.classList.remove('hidden');
      setTimeout(() => target.classList.add('hidden'), 10000);
    }
    if (other) other.classList.add('hidden');
  }

  /**
   * Restore order button and label after add-to-cart requests complete
   * @param {HTMLButtonElement | null} orderButton
   * @param {Element | null} labelEl
   * @param {string} originalLabel
   * @param {string | null} errorMessage
   * @param {boolean} [useOnetimePanel]
   */
  #restoreOrderButton(orderButton, labelEl, originalLabel, errorMessage, useOnetimePanel = false) {
    if (orderButton) {
      orderButton.disabled = this.calculateTotalCups() < this.minimumCups;
    }
    if (labelEl && originalLabel) {
      labelEl.textContent = originalLabel;
    }
    if (errorMessage) {
      this.#showError(errorMessage, useOnetimePanel);
    }
  }

  /**
   * Build cart/add.js payload (id, quantity, selling_plan) from DOM. One-time: no selling_plan; recurring: selected plan id.
   * When an extra product variant is configured it is appended as a single unit with no selling plan.
   */
  #buildAddToCartItems() {
    const sellingPlanId = this.#getSelectedSellingPlanId();
    const items = [];

    for (const item of this.container.querySelectorAll('.build-order-product-item')) {
      const qtyInput = item.querySelector('.nf-quantity-input');
      const displayQty = this.#normalizeQty(parseInt(qtyInput?.value, 10));
      const qty = this.#toCartQty(displayQty);
      if (qty <= 0) continue;
      const variantId = item.dataset.variantId;
      if (!variantId) continue;
      const line = { id: variantId, quantity: qty };
      if (sellingPlanId) line.selling_plan = sellingPlanId;
      if (this.extraProductVariantId) {
        line.properties = { [BUILD_ORDER_PARENT_LINE_PROPERTY_KEY]: '1' };
      }
      items.push(line);
    }

    if (this.extraProductVariantId) {
      const giftLine = { id: this.extraProductVariantId, quantity: 1 };
      if (this.subscriptionDiscountCode) {
        giftLine.properties = { [this.giftLineItemPropertyKey]: this.subscriptionDiscountCode };
      }
      items.push(giftLine);
    }

    return items;
  }

  /**
   * Get section IDs for cart drawer/items refresh (same as product-form)
   */
  #getCartSectionIds() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const ids = [];
    cartItemsComponents.forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.push(el.dataset.sectionId);
      }
    });
    return ids.join(',');
  }

  /**
   * Add all items to cart in one request via cart/add.js (items array + optional selling_plan). Returns { ok, data, status }.
   */
  async #addAllItemsToCart(items, options = {}) {
    const payload = {
      items: items.map((item) => {
        const line = { id: item.id, quantity: item.quantity };
        if (item.selling_plan) line.selling_plan = item.selling_plan;
        if (item.properties && Object.keys(item.properties).length > 0) line.properties = item.properties;
        return line;
      }),
    };
    if (options.sections) payload.sections = options.sections;
    const url = typeof Theme !== 'undefined' && Theme?.routes?.cart_add_url ? Theme.routes.cart_add_url : '/cart/add.js';
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const data = await response.json().catch(() => ({}));
    return { ok: response.ok, data, status: response.status };
  }

  /**
   * Handle order submission: bulk add via cart/add.js, then redirect (cart page) or refresh drawer (cart drawer).
   */
  async handleOrderSubmit() {
    const totalCups = this.calculateTotalCups();
    const isOnetime = this.orderType === 'onetime';

    if (totalCups < this.minimumCups) {
      const difference = this.minimumCups - totalCups;
      const errorTemplate = this.isCups ? this.errorAddMoreCups : this.errorAddMoreBeans;
      this.#showError(errorTemplate.replace(/\{\{\s*count\s*\}\}/, String(difference)), isOnetime);
      return;
    }

    this.#syncProductForms();

    const items = this.#buildAddToCartItems();
    if (items.length === 0) {
      this.#showError(this.errorNoProducts, isOnetime);
      return;
    }

    const orderButton = /** @type {HTMLButtonElement | null} */ (
      isOnetime
        ? this.container.querySelector('.build-order-onetime-submit-btn')
        : this.container.querySelector('.build-order-submit-btn[data-order-type="recurring"]')
    );
    const labelEl = isOnetime
      ? this.container.querySelector('.build-order-onetime-submit-btn-label')
      : this.container.querySelector('.build-order-submit-btn-label');
    const originalLabel = labelEl?.textContent ?? '';

    if (orderButton) {
      orderButton.disabled = true;
      if (labelEl) labelEl.textContent = this.loadingLabel;
    }

    const sections = this.addToCartBehavior === 'cart_drawer' ? this.#getCartSectionIds() : '';
    const { ok, data } = await this.#addAllItemsToCart(items, sections ? { sections } : {});

    if (!ok) {
      const lastErrorMessage = data?.description ?? data?.message ?? this.errorGeneric;
      this.#restoreOrderButton(orderButton, labelEl, originalLabel, lastErrorMessage, isOnetime);
      return;
    }

    const totalAdded = items.reduce((sum, item) => sum + item.quantity, 0);

    if (this.addToCartBehavior === 'cart_drawer') {
      if (!isOnetime && this.subscriptionDiscountCode) {
        await this.#applyDiscountCode(this.subscriptionDiscountCode);
      }

      document.dispatchEvent(
        new CustomEvent(CART_UPDATE_EVENT, {
          bubbles: true,
          detail: {
            resource: {},
            sourceId: 'build-order',
            data: {
              source: 'build-order',
              itemCount: totalAdded,
              sections: data?.sections,
            },
          },
        })
      );
      this.#restoreOrderButton(orderButton, labelEl, originalLabel, null, isOnetime);
      return;
    }

    this.#restoreOrderButton(orderButton, labelEl, originalLabel, null, isOnetime);
    const cartUrl = typeof Theme !== 'undefined' && Theme?.routes?.cart_url ? Theme.routes.cart_url : '/cart';

    if (!isOnetime && this.subscriptionDiscountCode) {
      window.location.href = `/discount/${encodeURIComponent(this.subscriptionDiscountCode)}?redirect=${encodeURIComponent(cartUrl)}`;
      return;
    }

    window.location.href = cartUrl;
  }

  /**
   * Apply a discount code to the current session by visiting the Shopify discount endpoint.
   * For cart-drawer flows this is done silently via fetch; for cart-page flows the caller
   * navigates directly to the /discount/ URL so this method is only needed for drawer mode.
   * @param {string} code
   */
  async #applyDiscountCode(code) {
    try {
      await fetch(`/discount/${encodeURIComponent(code)}`, { redirect: 'follow' });
    } catch {
      // Best-effort – discount will still be applicable at checkout
    }
  }
}

/**
 * Handle click on buttons with button-type="coffiecups" or "coffiebeans" and week: set build-order frequency and trigger save-percentage update.
 * @param {Event} e
 */
const handleBuildOrderFrequencyButtonClick = (e) => {
  // Find button with button-type attribute (coffiecups or coffiebeans) and week attribute
  const btn = e.target.closest?.('[button-type][week]');
  if (!btn) return;
  
  const buttonType = btn.getAttribute('button-type');
  const week = btn.getAttribute('week');
  if (!buttonType || !week) return;

  // Map button-type to frequency-select attribute value
  const frequencySelectAttr = buttonType === 'coffiecups' 
    ? 'frequency-select-coffiecups' 
    : 'frequency-select-coffiebeans';

  // Find the frequency select with the matching attribute
  const frequencySelect = document.querySelector(`.build-order-frequency-select[frequency-select="${frequencySelectAttr}"]`);
  
  if (!frequencySelect) return;
  if (frequencySelect.value === week) return;
  
  frequencySelect.value = week;
  frequencySelect.dispatchEvent(new Event('change', { bubbles: true }));
};

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  const buildOrderSections = document.querySelectorAll('.build-order-section');

  buildOrderSections.forEach((section) => {
    new BuildOrderComponent(section);
  });

  document.addEventListener('click', handleBuildOrderFrequencyButtonClick);

  const kiesJeFavorieteContainer = document.querySelector('.kies-je-favoriete');
  /**
   * Update the "Kies je favoriete ..." copy based on active tab.
   * @param {'cups' | 'beans' | 'thee'} word
   */
  const updateKiesJeFavorieteCopy = (word) => {
    if (!kiesJeFavorieteContainer) return;
    const paragraphs = kiesJeFavorieteContainer.querySelectorAll('p');
    for (const p of paragraphs) {
      const text = (p.textContent || '').replace(/\s+/g, ' ').trim();
      if (!text) continue;

      if (text === 'Kies je favoriete cups' || text.startsWith('Kies je favoriete ')) {
        p.textContent = `Kies je favoriete ${word}`;
        return;
      }
    }
  };

  const tabContainer = document.querySelector('.kies-voor-bonen-cups-of-thee');

  /**
   * @param {string} name
   * @returns {HTMLAnchorElement | null}
   */
  const getTabLink = (name) => {
    if (!tabContainer) return null;
    return /** @type {HTMLAnchorElement | null} */ (
      tabContainer.querySelector(`a[data-name="${name}"], a[attr-name="${name}"]`)
    );
  };

  const cupsLink = getTabLink('ga-verder-met-cups');
  const beansLink = getTabLink('ga-verder-met-koffiebonen');
  const losseTheeLink = getTabLink('ga-verder-met-losse-thee');

  const CHECKMARK_SVG =
    '<svg width="12" height="9" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.1015 8.629L0 4.5275L1.261 3.26667L4.1015 6.10717L10.2087 0L11.4697 1.26083L4.1015 8.629Z" fill="#6E9C2E"/></svg>';

  /**
   * @param {HTMLAnchorElement | null} link
   * @returns {HTMLSpanElement | null}
   */
  const ensureTabCheckEl = (link) => {
    if (!link) return null;
    const existing = /** @type {HTMLSpanElement | null} */ (link.querySelector('[data-build-order-tab-check]'));
    if (existing) return existing;

    const el = document.createElement('span');
    el.setAttribute('data-build-order-tab-check', 'true');
    el.setAttribute('aria-hidden', 'true');
    el.style.display = 'none';
    el.style.alignItems = 'center';
    el.style.justifyContent = 'center';
    el.style.marginRight = '8px';
    el.style.verticalAlign = 'middle';
    el.innerHTML = CHECKMARK_SVG;

    const labelSpan = link.querySelector('span');
    if (labelSpan) {
      link.insertBefore(el, labelSpan);
    } else {
      link.prepend(el);
    }
    return el;
  };

  /**
   * @param {HTMLAnchorElement | null} link
   * @param {boolean} isSelected
   */
  const setTabSelected = (link, isSelected) => {
    if (!link) return;
    link.setAttribute('data-state', isSelected ? 'selected' : 'dimmed');
    const checkEl = ensureTabCheckEl(link);
    if (checkEl) checkEl.style.display = isSelected ? 'inline-flex' : 'none';
  };

  const buildOrderCupsSection = document.querySelector('.build-order-coffiecups');
  const buildOrderBeansSection = document.querySelector('.build-order-coffiebeans');
  const buildOrderLosseTheeSection = document.querySelector('.build-order-losse-thee');

  const buildOrderCupsWrapper = buildOrderCupsSection
    ? /** @type {HTMLElement | null} */ (buildOrderCupsSection.closest('.shopify-section'))
    : null;
  const buildOrderBeansWrapper = buildOrderBeansSection
    ? /** @type {HTMLElement | null} */ (buildOrderBeansSection.closest('.shopify-section'))
    : null;
  const buildOrderLosseTheeWrapper = buildOrderLosseTheeSection
    ? /** @type {HTMLElement | null} */ (buildOrderLosseTheeSection.closest('.shopify-section'))
    : null;

  /**
   * @param {'cups' | 'beans' | 'losseThee'} activeTab
   */
  const setActiveTab = (activeTab) => {
    if (activeTab === 'beans' && !buildOrderBeansWrapper) {
      return;
    }

    if (activeTab === 'cups' && !buildOrderCupsWrapper) {
      return;
    }

    if (activeTab === 'losseThee' && !buildOrderLosseTheeWrapper) {
      return;
    }

    updateKiesJeFavorieteCopy(
      activeTab === 'beans' ? 'beans' : activeTab === 'losseThee' ? 'thee' : 'cups'
    );

    if (buildOrderCupsWrapper) {
      buildOrderCupsWrapper.style.display = activeTab === 'cups' ? '' : 'none';
    }

    if (buildOrderBeansWrapper) {
      buildOrderBeansWrapper.style.display = activeTab === 'beans' ? '' : 'none';
    }

    if (buildOrderLosseTheeWrapper) {
      buildOrderLosseTheeWrapper.style.display = activeTab === 'losseThee' ? '' : 'none';
    }

    if (cupsLink) {
      cupsLink.classList.toggle('is-active', activeTab === 'cups');
    }

    if (beansLink) {
      beansLink.classList.toggle('is-active', activeTab === 'beans');
    }

    if (losseTheeLink) {
      losseTheeLink.classList.toggle('is-active', activeTab === 'losseThee');
    }

    setTabSelected(cupsLink, activeTab === 'cups');
    setTabSelected(beansLink, activeTab === 'beans');
    setTabSelected(losseTheeLink, activeTab === 'losseThee');
  };

  /** @type {'cups' | 'beans' | 'losseThee' | null} */
  let initialTab = null;
  if (tabContainer) {
    const firstLink = tabContainer.querySelector('a[data-name], a[attr-name]');
    const attrName = firstLink?.getAttribute('data-name') || firstLink?.getAttribute('attr-name') || '';
    if (attrName === 'ga-verder-met-koffiebonen') {
      initialTab = 'beans';
    } else if (attrName === 'ga-verder-met-losse-thee') {
      initialTab = 'losseThee';
    } else if (attrName === 'ga-verder-met-cups') {
      initialTab = 'cups';
    }
  }

  setActiveTab(initialTab || 'cups');

  if (cupsLink) {
    cupsLink.addEventListener('click', (event) => {
      event.preventDefault();
      setActiveTab('cups');
      if (buildOrderCupsWrapper) {
        buildOrderCupsWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  if (beansLink) {
    beansLink.addEventListener('click', (event) => {
      event.preventDefault();
      setActiveTab('beans');
      if (buildOrderBeansWrapper) {
        buildOrderBeansWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  if (losseTheeLink) {
    losseTheeLink.addEventListener('click', (event) => {
      event.preventDefault();
      setActiveTab('losseThee');
      if (buildOrderLosseTheeWrapper) {
        buildOrderLosseTheeWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }
});
} // End of duplicate prevention guard